import {
  Container,
  useSearchNft,
  Center,
  Spinner,
  DefaultAvatar,
} from '@dapperlabs/core-fe';
import { useRouter } from 'next/router';
import { useRef, useState, useEffect } from 'react';
import { FS } from 'src/general/constants/finiteStates';
import { useQueryStates } from 'src/general/hooks';
import { useTranslation } from 'src/lib/translations';
import { getTeamData } from 'src/modules/Branding';
import { ShortNames } from 'src/modules/Branding/teamData';
import {
  UserProfile,
  UserProfileWithoutPii,
} from 'src/modules/Queries/generated';
import {
  SEARCH_PACK_NFT_TOTAL_COUNT,
  SEARCH_GOLAZOS_NFT_TOTAL_COUNT,
  searchGolazosNftByOwnerAddress,
  searchPackNftByOwnerAddress,
} from 'src/modules/Queries/searchNft';
import {
  Box,
  Flex,
  Grid,
  Heading,
  Text,
  IconButton,
  IconShare,
  Image,
  Tooltip,
} from 'src/theme';

interface Props {
  isOwnProfile?: boolean;
  locale: string;
  userProfile: UserProfile | UserProfileWithoutPii | null;
}

export const MyCollectionHeader = ({
  locale,
  userProfile,
  isOwnProfile = false,
}: Props) => {
  const { t } = useTranslation();
  const router = useRouter();

  // Fallback to username in URL (`/user/[username]`) when user is not found
  const { username: usernameParam } = router.query;
  const username = userProfile?.username ?? usernameParam;
  const avatarUrl = userProfile?.profileImageUrl ?? '';
  const favoriteClub = userProfile?.favoriteClub;
  const userFlowAddress = userProfile?.flowAddress;

  const accountCreatedAt = userProfile?.createdAt;
  const joinedDate = new Date(accountCreatedAt).toLocaleDateString(locale, {
    month: 'long',
    year: 'numeric',
  });

  const teamData = getTeamData(ShortNames[favoriteClub]);
  const favoriteTeamName = teamData?.shortName ?? '';
  const logoPath = teamData?.logoPath ?? '';

  const { totalCount: momentsCount, state: momentCountState } = useSearchNft({
    aggregationResponseType: 'searchGolazosNft',
    options: { skip: !userFlowAddress },
    query: SEARCH_GOLAZOS_NFT_TOTAL_COUNT,
    // flowAddress may not be available right now.
    variables: searchGolazosNftByOwnerAddress(userFlowAddress),
  });
  // Look for pack IDs
  const { totalCount: packsCount, state: packCountState } = useSearchNft({
    aggregationResponseType: 'searchPackNft',
    options: { skip: !userFlowAddress },
    query: SEARCH_PACK_NFT_TOTAL_COUNT,
    variables: searchPackNftByOwnerAddress(userFlowAddress),
  });
  const queryState = useQueryStates({
    states: [momentCountState, packCountState],
  });

  // Copy URL to clipboard
  const tooltipRef = useRef();
  const [isShareTooltipOpen, setIsShareTooltipOpen] = useState(false);

  const handleShareCollection = () => {
    setIsShareTooltipOpen(true);
    navigator.clipboard.writeText(
      `${process.env.NEXT_PUBLIC_SITE_URL}/user/${username}`,
    );
  };

  useEffect(() => {
    if (!isShareTooltipOpen) return;
    const timer = setTimeout(() => {
      setIsShareTooltipOpen(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, [isShareTooltipOpen]);

  if (userProfile && (queryState === FS.LOADING || queryState === FS.IDLE)) {
    return (
      <Center minHeight={{ base: '225px', md: '165px' }}>
        <Spinner />
      </Center>
    );
  }

  return (
    <Box pt={{ base: 6, md: 12 }}>
      <Container maxW={{ md: '80%' }} px={{ base: 4, md: 0 }}>
        <Grid
          gridRowGap={{ base: 4, md: 2 }}
          gridColumnGap={{ md: 6 }}
          gridTemplateColumns={{ base: 'repeat(3, auto)', md: 'auto 1fr auto' }}
          gridTemplateAreas={{
            base: `
              "avatar . share"
              "user user user"
              "stats stats stats"
            `,
            md: `
              "avatar user share"
              "avatar stats stats"
            `,
          }}
        >
          <Flex
            gridArea='avatar'
            overflow='hidden'
            w={{ base: '60px', md: '100px' }}
            h={{ base: '60px', md: '100px' }}
            borderRadius='lg'
          >
            {avatarUrl ? (
              <Image objectFit='contain' alt='Your avatar' src={avatarUrl} />
            ) : (
              <DefaultAvatar
                w={{ base: '60px', md: '100px' }}
                h={{ base: '60px', md: '100px' }}
              />
            )}
          </Flex>
          <Flex justifyContent='flex-end' gridArea='share'>
            <Tooltip
              ref={tooltipRef}
              isOpen={isShareTooltipOpen}
              label={t('MyCollection.Share.Copied')}
              variant='basic'
            >
              <IconButton
                color='white'
                bg='rgba(138, 148, 166, 0.25)'
                aria-label='Share'
                backdropFilter='blur(15px)'
                icon={<IconShare w={4} h={4} />}
                onClick={handleShareCollection}
                size='md'
              />
            </Tooltip>
          </Flex>
          <Flex flexDirection='column' gap={2} gridArea='user'>
            <Flex flexWrap='wrap' flexDirection='column' gap={2}>
              <Flex
                alignItems={{ base: 'center', md: 'flex-start' }}
                flexWrap='wrap'
                gap={{ base: 2, md: 5 }}
              >
                {username && (
                  <Heading size={{ base: 'sm', md: 'md' }}>{username}</Heading>
                )}
                {favoriteTeamName && (
                  <Flex
                    key={favoriteTeamName}
                    alignItems='center'
                    h={8}
                    px={3}
                    bg='rgba(138, 148, 166, 0.25)'
                    borderRadius='3xl'
                    cursor='auto'
                    backdropFilter='blur(15px)'
                  >
                    <Image
                      width={8}
                      mr={1}
                      ml='-5%'
                      alt={`${favoriteTeamName} logo`}
                      src={logoPath}
                    />
                    <Text fontSize='xs' fontWeight='bold' bg='transparent'>
                      {favoriteTeamName}
                    </Text>
                  </Flex>
                )}
              </Flex>
              {accountCreatedAt && (
                <Text color='gray.300' fontSize='xs'>
                  {t('MyCollection.Header.Joined', { monthYear: joinedDate })}
                </Text>
              )}
            </Flex>
          </Flex>
          <Flex gap={{ base: 2, md: 3 }} gridArea='stats' overflowX='auto'>
            {typeof momentsCount === 'number' && (
              <StatCard name='Moments' value={momentsCount} />
            )}
            {typeof packsCount === 'number' && (
              <StatCard name='Packs' value={packsCount} />
            )}
          </Flex>
        </Grid>
        {isOwnProfile && (
          <Flex
            justifyContent='flex-end'
            mt={4}
            color='#D6DAEB'
            fontSize='14px'
            fontWeight='400'
            textDecoration='underline'
            cursor='pointer'
            onClick={() => router.push('activity')}
          >
            {t('MyMoments.Tabs.SeeAllTransactions')}
          </Flex>
        )}
      </Container>
    </Box>
  );
};

interface StatCardProps {
  name: string | number;
  value: string | number;
}

const StatCard = ({ name, value }: StatCardProps) => {
  return (
    <Box
      flexShrink='0'
      flexBasis={{ base: '116px', md: '146px' }}
      p={2}
      bg='gray.900'
      borderRadius='xl'
    >
      <Text mb={1} fontFamily='mono' fontSize='xs' variant='label'>
        {name}
      </Text>
      <Heading size='xs'>{value}</Heading>
    </Box>
  );
};
